import {DragAndDrop, type OnDropArgs, useSortable} from '@github-ui/drag-and-drop'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import {testIdProps} from '@github-ui/test-id-props'
import {type ContainerBreakpointFunction, useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {VisuallyHidden} from '@github-ui/visually-hidden'
import {GrabberIcon, KebabHorizontalIcon, PencilIcon, XIcon} from '@primer/octicons-react'
import type {SxProp} from '@primer/react'
import {ActionList, ActionMenu, Box, Button, Flash, FormControl, IconButton, TextInput} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useRef, useState} from 'react'

import type {NewSingleOption, PersistedOption} from '../../../api/columns/contracts/single-select'
import {replaceShortCodesWithEmojis} from '../../../helpers/emojis'
import {getInitialState} from '../../../helpers/initial-state'
import {emptySingleSelectOption, type SettingsOption} from '../../../helpers/new-column'
import {useEmojiAutocomplete} from '../../../hooks/common/use-emoji-autocomplete'
import {useReturnFocus} from '../../../hooks/common/use-return-focus'
import {Resources} from '../../../strings'
import {SanitizedHtml} from '../../dom/sanitized-html'
import {SingleSelectOptionModal} from './single-select-option-modal'
import {SingleSelectToken} from './single-select-token'

interface SingleSelectFormProps extends SxProp {
  options: Array<SettingsOption>
  canRemoveOption: boolean
  onAddOption: (option: NewSingleOption) => void
  onUpdateOption: (updatedOption: SettingsOption) => void
  onRemoveOption: (id: string) => void
  onDrop: ({dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>) => void
}

export const SingleSelectForm = ({
  options,
  onAddOption: external_onAddOption,
  onUpdateOption,
  onRemoveOption,
  canRemoveOption,
  onDrop,
  sx,
}: SingleSelectFormProps) => {
  const {
    projectLimits: {singleSelectColumnOptionsLimit},
  } = getInitialState()

  const dropRef = useRef<HTMLOListElement>(null)
  const optionsLimitReached = options.length >= singleSelectColumnOptionsLimit

  const selectInputByIndex = (index: number) =>
    dropRef.current?.querySelector<HTMLInputElement>(`input[data-option-index='${index}']`)

  const onAddOption = (option: NewSingleOption = emptySingleSelectOption) => {
    external_onAddOption(option)
    setTimeout(() => selectInputByIndex(options.length)?.focus())
  }

  const [container, containerRef] = useState<HTMLDivElement | null>(null)
  const breakpoint = useContainerBreakpoint(container)

  return (
    <Box
      sx={{
        borderStyle: 'solid',
        borderWidth: breakpoint([0, 1]),
        borderColor: 'border.default',
        borderRadius: 2,
        ...sx,
      }}
      ref={containerRef}
    >
      <DragAndDrop
        items={options.map(option => {
          return {id: option.id, title: option.name}
        })}
        onDrop={onDrop}
        style={{listStyle: 'none'}}
        aria-label={'Single select options'}
      >
        {options.slice(0, singleSelectColumnOptionsLimit).map((option, index) => (
          <DragAndDrop.Item
            id={option.id}
            index={index}
            title={option.name}
            key={option.id}
            UNSAFE_hideSortableItemTrigger
          >
            <SingleSelectItem
              key={option.id}
              option={option}
              index={index}
              enableOptionRemoval={canRemoveOption}
              onRemove={() => onRemoveOption(option.id)}
              onChange={onUpdateOption}
              breakpoint={breakpoint}
            />
          </DragAndDrop.Item>
        ))}
      </DragAndDrop>

      <Box sx={{p: breakpoint([0, 3]), pt: breakpoint([2, 3])}}>
        {optionsLimitReached ? (
          <Flash variant="warning" sx={{p: 2}}>
            {Resources.singleSelectOptionLimitWarning(singleSelectColumnOptionsLimit)}
          </Flash>
        ) : (
          <NewItemCreator onCreate={onAddOption} />
        )}
      </Box>
    </Box>
  )
}

interface NewItemCreatorProps {
  onCreate: (option?: NewSingleOption) => void
}

const NewItemCreator = ({onCreate}: NewItemCreatorProps) => {
  const [name, setName] = useState('')

  const isValid = name.trim() !== ''

  const onSubmit = () => {
    if (isValid) {
      onCreate({...emptySingleSelectOption, name: replaceShortCodesWithEmojis(name)})
      setName('')
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Enter') {
      event.preventDefault()
      onSubmit()
    }
  }
  const inputProps = useIgnoreKeyboardActionsWhileComposing(onKeyDown)
  const autocompleteProps = useEmojiAutocomplete()

  return (
    <Box as="fieldset" sx={{display: 'flex', gap: 2, border: 0, padding: 0, margin: 0}} onSubmit={onSubmit}>
      <VisuallyHidden as="legend">Add option</VisuallyHidden>
      <FormControl sx={{flex: 1}}>
        <FormControl.Label visuallyHidden>Label text</FormControl.Label>
        <InlineAutocomplete {...autocompleteProps} sx={{width: '100%'}}>
          <TextInput
            block
            value={name}
            onChange={ev => setName(ev.target.value)}
            placeholder="Add option…"
            autoComplete="false"
            {...inputProps}
          />
        </InlineAutocomplete>
      </FormControl>
      <Button onClick={onSubmit} disabled={!isValid}>
        Add
      </Button>
    </Box>
  )
}

interface SingleSelectItemProps {
  option: PersistedOption | SettingsOption
  index: number
  enableOptionRemoval: boolean
  onRemove: React.MouseEventHandler
  onChange: (updatedOption: SettingsOption) => void
  breakpoint: ContainerBreakpointFunction
}

const boxWithDropSashStyle: BetterSystemStyleObject = {
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.default',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}

function SingleSelectItem({index, option, enableOptionRemoval, onRemove, onChange, breakpoint}: SingleSelectItemProps) {
  const {setNodeRef} = useSortable({
    id: option.id,
    data: {metadata: {id: option.id}},
  })

  const [editModalVisible, setEditModalVisible] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [reorderModalVisible, setReorderModalVisable] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)

  useReturnFocus(editModalVisible, editButtonRef)
  useReturnFocus(reorderModalVisible, editButtonRef)

  return (
    <Box
      key={option.id}
      ref={setNodeRef}
      sx={{
        px: breakpoint([0, 3]),
        py: breakpoint([1, 3]),
        borderBottomWidth: breakpoint([0, 1]),
        ...boxWithDropSashStyle,
      }}
      {...testIdProps('singleSelectItem')}
    >
      <DragAndDrop.DragTrigger />
      <SingleSelectToken option={option} />
      <SanitizedHtml
        sx={{
          flex: 1,
          px: 2,
          color: 'fg.subtle',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          m: 0,
        }}
        as="p"
      >
        {'descriptionHtml' in option ? option.descriptionHtml : option.description}
      </SanitizedHtml>

      <ActionMenu onOpenChange={setMenuOpen} open={menuOpen} anchorRef={editButtonRef}>
        <ActionMenu.Anchor>
          <IconButton
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label={`Open field actions for ${option.name}`}
            size="small"
            sx={{color: 'fg.muted'}}
            {...testIdProps('single-select-item-menu-button')}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.Item
              onSelect={() => setEditModalVisible(true)}
              aria-label={`Open edit dialog for ${option.name}`}
              {...testIdProps('single-select-item-edit-button')}
            >
              <ActionList.LeadingVisual>
                <PencilIcon />
              </ActionList.LeadingVisual>
              Edit option
            </ActionList.Item>
            <ActionList.Item
              aria-label={`Other actions for ${option.name}`}
              onSelect={() => setReorderModalVisable(true)}
              {...testIdProps('single-select-item-move-button')}
            >
              <ActionList.LeadingVisual>
                <GrabberIcon />
              </ActionList.LeadingVisual>
              Move
            </ActionList.Item>
            {enableOptionRemoval && (
              <>
                <ActionList.Divider />
                <ActionList.Item
                  variant="danger"
                  onClick={onRemove}
                  aria-label={`Remove ${option.name}`}
                  {...testIdProps('single-select-item-delete-button')}
                >
                  <ActionList.LeadingVisual>
                    <XIcon />
                  </ActionList.LeadingVisual>
                  Remove option
                </ActionList.Item>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      {editModalVisible && (
        <SingleSelectOptionModal
          initialOption={option}
          onCancel={() => setEditModalVisible(false)}
          onSave={updatedOption => {
            setEditModalVisible(false)
            onChange({...option, ...updatedOption})
          }}
        />
      )}
      {reorderModalVisible && (
        <DragAndDrop.MoveModal title={option.name} currentIndex={index} closeDialog={setReorderModalVisable} />
      )}
    </Box>
  )
}

try{ SingleSelectForm.displayName ||= 'SingleSelectForm' } catch {}
try{ NewItemCreator.displayName ||= 'NewItemCreator' } catch {}
try{ SingleSelectItem.displayName ||= 'SingleSelectItem' } catch {}